
import { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { analyzePageWithOpenAI, logAnalysisResult } from '@/services/deepAnalysisService';
import { 
  AnalysisState, 
  DeepAnalysisResult, 
  UsePageAnalysisProps
} from '@/types/analysisTypes';
import { Page } from '@/types/crawler';
import { getScoreValue } from '@/components/deep-analysis/utils';
import { API_KEYS } from '@/utils/apiKeys';

// Dynamic validation function - handle string numbers and zero scores
const isScoreValid = (key: string, value: any) => {
  if (!key || key.trim() === '') return false;
  if (value === undefined || value === null) return false;
  
  // Handle both number and string numbers, allow zero scores
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  
  // Handle object with score property
  if (typeof value === 'object' && value.score !== undefined) {
    return Number.isFinite(value.score);
  }
  
  // Handle stringified numbers
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return !isNaN(parsed) && Number.isFinite(parsed);
  }
  
  return false;
};

// Initial state for the analysis
const initialAnalysisState: AnalysisState = {
  isAnalyzing: false,
  analysisComplete: false,
  currentPage: undefined,
  result: null,
  error: null
};

/**
 * Custom hook for page analysis functionality
 */
export function usePageAnalysis({ featureId = 'deepAnalysis' }: UsePageAnalysisProps = {}) {
  const [state, setState] = useState<AnalysisState>(initialAnalysisState);

  /**
   * Sets the analysis state to loading
   */
  const setAnalysisLoading = useCallback((pageUrl: string) => {
    setState(prev => ({ 
      ...prev, 
      isAnalyzing: true, 
      currentPage: pageUrl,
      analysisComplete: false,
      error: null
    }));
  }, []);

  /**
   * Log the result for debugging (sanitization handled by resultProcessingService)
   */
  const logResult = useCallback((result: DeepAnalysisResult): DeepAnalysisResult => {
    console.log('🚨 usePageAnalysis - Result received:', {
      ai_optimization_present: 'ai_optimization_score' in (result.category_scores || {}),
      keys: Object.keys(result.category_scores || {}),
      overall_score: result.overall_score
    });
    
    return result;
  }, []);

  /**
   * Sets the analysis state to completed with results
   */
  const setAnalysisComplete = useCallback((result: DeepAnalysisResult) => {
    console.log('Analysis completed, setting state with results');
    logAnalysisResult(result);
    
    // Clean rebuild: Deep freeze the result to prevent any mutations
    const frozenResult = Object.freeze(JSON.parse(JSON.stringify(result)));
    console.log('🔒 Frozen result - category_scores:', Object.keys(frozenResult.category_scores || {}));
    
    setState(prev => ({ 
      ...prev, 
      isAnalyzing: false, 
      analysisComplete: true,
      result: frozenResult,
      error: frozenResult.error || null
    }));
  }, [logResult]);

  /**
   * Sets the analysis state to error
   */
  const setAnalysisError = useCallback((errorMessage: string) => {
    console.error('Analysis failed with error:', errorMessage);
    
    setState(prev => ({ 
      ...prev, 
      isAnalyzing: false,
      analysisComplete: false,
      error: errorMessage
    }));
  }, []);

  /**
   * Performs page analysis using OpenAI
   */
  const performAnalysis = useCallback(async (page: Page): Promise<DeepAnalysisResult | null> => {
    try {
      console.log('Starting page analysis for:', page.url);
      // Use API key directly from the API_KEYS object
      const apiKey = API_KEYS.openai[featureId === 'deepAnalysis' ? 'deepAnalysis' : 'recommended'];
      // Use appropriate model for the feature
      const model = featureId === 'deepAnalysis' ? 'gpt-4o' : 'gpt-4o-mini';
      
      // Log API key info (not the full key) for debugging
      console.log(`Analysis parameters: model=${model}, contentLength=${page.content?.length || 0}, apiKeyPresent=${apiKey ? 'Yes' : 'No'}, apiKeyLength=${apiKey?.length || 0}`);
      
      // Verify API key has correct format
      if (!apiKey || !apiKey.startsWith('sk-')) {
        const errorMsg = 'Invalid API key format. API keys should start with "sk-"';
        console.error(errorMsg);
        setAnalysisError(errorMsg);
        return null;
      }
      
      // Pass the page URL to enable caching by URL
      const result = await analyzePageWithOpenAI(apiKey, model, page);
      
      // Log results (sanitization handled by resultProcessingService)
      return result ? logResult(result) : null;
    } catch (error) {
      const errorMessage = (error as Error).message || 'Unknown error occurred';
      console.error('Analysis error:', error);
      setAnalysisError(errorMessage);
      return null;
    }
  }, [featureId, setAnalysisError, logResult]);

  /**
   * Analyze a single page
   */
  const analyzePage = useCallback(async (page: Page) => {
    setAnalysisLoading(page.url);

    const result = await performAnalysis(page);
    
    if (result) {
      // Check if result has an error
      if (result.error) {
        setAnalysisComplete(result);
        toast.error(`Analysis error: ${result.error}`);
        console.error('Analysis returned an error:', result.error);
        return result;
      }
      
      // Check if result has valid scores
      const hasValidScores = result.overall_score > 0 || 
        Object.entries(result.category_scores).some(([_, score]) => {
          return getScoreValue(score) > 0;
        });
      
      if (hasValidScores) {
        setAnalysisComplete(result);
        toast.success('Analysis complete!');
        console.log('Analysis completed successfully:', result);
      } else {
        console.warn('Analysis returned zero scores:', result);
        setAnalysisComplete(result);
        toast.warning('Analysis completed but returned zero scores. Check console for details.');
      }
      
      return result;
    } else {
      setAnalysisError('Failed to analyze page. Please try again later.');
      toast.error('Analysis failed. Please try again later.');
      return null;
    }
  }, [performAnalysis, setAnalysisComplete, setAnalysisError, setAnalysisLoading]);

  /**
   * Get detailed analysis for a specific page
   */
  const getPageAnalysis = useCallback(async (page: Page): Promise<DeepAnalysisResult | null> => {
    // Just analyze the page directly
    return analyzePage(page);
  }, [analyzePage]);

  /**
   * Reset the analysis state
   */
  const reset = useCallback(() => {
    setState(initialAnalysisState);
  }, []);

  return {
    ...state,
    analyzePage,
    getPageAnalysis,
    reset
  };
}
